import { useCallback } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import type { GENDER, SALUTATION } from 'cpool-constants';
import { GENDERS, SALUTATIONS } from 'cpool-constants';
import { dateTo_DD_MM_YYYY_dot } from 'cpool-utils';
import type { TFunction } from 'i18next';
import type { FieldValues, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { capitalizeWords } from '../utils/capitalizeWords';
import { useSchufa } from '~/features/leadDetails/components/BankCreditRequest/NewCreditRequest/RefinancePlanner/hooks/useSchufa.ts';
import useLead from '~/features/leadDetails/hooks/useLead.ts';

type Params<T extends FieldValues> = {
    antragsteller: 1 | 2;
    setValue: UseFormSetValue<T>;
    addressKind?: 'anschrift' | 'vorherigeAnschrift';
    applicantId?: string;
};

const SET_VALUE_OPTIONS = { shouldDirty: true, shouldValidate: true } as const;

function formatName(raw: string | undefined): string {
    if (!raw?.trim()) return '-';
    return capitalizeWords(raw);
}

function formatCity(raw: string | undefined): string {
    if (!raw?.trim()) return '';
    return capitalizeWords(raw);
}

function formatStreet(raw: string | undefined): { display: string; houseNumber: string; street: string } {
    if (!raw?.trim()) return { display: '-', houseNumber: '', street: '' };
    const parts = raw.trim().split(/\s+/);
    const houseNumber = parts.length > 1 ? parts[parts.length - 1] : '';
    const streetParts = houseNumber ? parts.slice(0, -1) : parts;
    const street = capitalizeWords(streetParts.join(' '));
    return { display: `${street} ${houseNumber}`.trim(), houseNumber, street };
}

function formatBirthdate(raw: string | undefined): { display: string; date: Date | undefined } {
    if (!raw) return { date: undefined, display: '-' };
    return { date: new Date(raw), display: dateTo_DD_MM_YYYY_dot(new Date(raw)) };
}

function formatGender(raw: GENDER | undefined, t: TFunction): { display: string; gender: SALUTATION } {
    if (!raw) return { display: '-', gender: SALUTATIONS.DIVERS };
    const value = raw === GENDERS.FEMALE ? SALUTATIONS.FRAU : SALUTATIONS.HERR;
    return { display: t(`gender.${value}`), gender: value };
}

export function useSchufaTransfer<T extends FieldValues>({
    addressKind,
    antragsteller,
    applicantId,
    setValue,
}: Params<T>) {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { data: lead } = useLead(leadId);
    const { t } = useTranslation();

    const resolvedApplicantId =
        applicantId ?? (antragsteller === 1 ? lead?.antragsteller1.id : lead?.antragsteller2?.id);
    const { data } = useSchufa({ applicantId: resolvedApplicantId, leadId });

    const schufaData = data?.applicantData;

    const set = (field: string, value: unknown) =>
        setValue(field as Parameters<typeof setValue>[0], value as never, SET_VALUE_OPTIONS);

    // ── Address ───────────────────────────────────────────────────────────────

    const schufaAddress =
        addressKind === 'vorherigeAnschrift' ? schufaData?.previousAddress : schufaData?.currentAddress;

    const formattedStreet = formatStreet(schufaAddress?.street);
    const formattedCity = formatCity(schufaAddress?.city);
    const formattedPostalCode = schufaAddress?.postalCode ?? '';
    const formattedCityZip =
        formattedPostalCode || formattedCity ? `${formattedPostalCode} ${formattedCity}`.trim() : '-';

    // ── Personal ───────────────────────────────────────────────────────────────

    const formattedFirstname = formatName(schufaData?.firstName);
    const formattedLastname = formatName(schufaData?.lastName);
    const formattedBirthdate = formatBirthdate(schufaData?.birthDate);
    const formattedBirthplace = formatName(schufaData?.birthPlace);
    const formattedGender = formatGender(schufaData?.gender, t);

    const isValueValid = (value: unknown) => value !== null && value !== undefined && value !== '' && value !== '-';

    const transferPersonalField = useCallback(
        (fieldName: string, value: unknown) => {
            if (!isValueValid(value)) return;
            set(`antragsteller${antragsteller}.personendaten.${fieldName}`, value);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [antragsteller, setValue],
    );

    const transferAddressField = useCallback(
        (fieldName: string, value: unknown) => {
            if (!isValueValid(value)) return;
            set(`antragsteller${antragsteller}.wohnsituation.${addressKind}.${fieldName}`, value);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [antragsteller, addressKind, setValue],
    );

    const transferStreet = () => {
        if (!formattedStreet.street) return;
        transferAddressField('strasse', formattedStreet.street);
        transferAddressField('hausnummer', formattedStreet.houseNumber);
    };

    const transferCityAndZip = () => {
        if (formattedCity) transferAddressField('ort', formattedCity);
        if (formattedPostalCode) transferAddressField('plz', formattedPostalCode);
    };

    const transferGender = () => {
        if (schufaData?.gender) transferPersonalField('anrede', formattedGender.gender);
    };

    const transferFirstname = () => {
        if (schufaData?.firstName) transferPersonalField('vorname', formattedFirstname);
    };

    const transferLastname = () => {
        if (schufaData?.lastName) transferPersonalField('nachname', formattedLastname);
    };

    const transferBirthdate = () => {
        if (schufaData?.birthDate) transferPersonalField('geburtsdatum', formattedBirthdate.date);
    };

    const transferBirthplace = () => {
        if (schufaData?.birthPlace) transferPersonalField('geburtsort', formattedBirthplace);
    };

    const transferAddress = () => {
        transferStreet();
        transferCityAndZip();
    };

    const transferPersonal = () => {
        transferGender();
        transferFirstname();
        transferLastname();
        transferBirthdate();
        transferBirthplace();
    };

    return {
        schufaBirthdate: formattedBirthdate.display,
        schufaBirthplace: formattedBirthplace,
        schufaCityZip: formattedCityZip,
        schufaFirstname: formattedFirstname,
        schufaGender: formattedGender.display,
        schufaLastname: formattedLastname,
        schufaStreet: formattedStreet.display,
        transferAddress,
        transferBirthdate,
        transferBirthplace,
        transferCityAndZip,
        transferFirstname,
        transferGender,
        transferLastname,
        transferPersonal,
        transferStreet,
    };
}
